import dayjs from 'dayjs';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import type { StageMessage } from './shared';

/**
 * "Período crítico" (critical period) banner — corn only, advisor-facing.
 *
 * The critical period isn't stored; it's derived as the estimated flowering date ± 15 days (the
 * app-wide convention). This banner is date-driven, not stage-gated — it can appear during any
 * late vegetative stage or flowering. Three sequential messages track the window:
 *
 * 1. From 5 days before the period starts until it starts ([flowering−20, flowering−15)).
 * 2. During the period until 5 days before it ends ([flowering−15, flowering+10)).
 * 3. The final 5 days ([flowering+10, flowering+15]) — with visit actions to estimate yield.
 *
 * Returns `null` for non-corn/non-advisor, when there's no estimated flowering date, or outside
 * the [flowering−20, flowering+15] window.
 */
export const useCriticPeriodStageMessage = ({
    cropMonitoring,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isValidatedBayerUserType = useIsValidatedBayerUserType();

    const corn = cropMonitoring?.cornCropMonitoring;
    const isCorn = Boolean(cropMonitoring?.active) && cropMonitoring?.crop === 'corn';
    const flowering = corn?.expectedFloweringDate ? dayjs(corn.expectedFloweringDate) : null;

    if (!isValidatedBayerUserType || !isCorn || !flowering?.isValid()) return null;

    const now = dayjs();
    const criticStart = flowering.subtract(15, 'day');
    const criticEnd = flowering.add(15, 'day');
    const nearStart = criticStart.subtract(5, 'day');
    const nearEnd = criticEnd.subtract(5, 'day');

    // Outside the whole [flowering−20, flowering+15] window → no banner.
    if (now.isBefore(nearStart, 'day') || now.isAfter(criticEnd, 'day')) return null;

    const floweringDate = flowering.format('DD/MM/YYYY');

    // 1. Approaching — 5 days before the period starts, until it starts.
    if (now.isBefore(criticStart, 'day')) {
        return {
            title: _('Se acerca el período crítico del lote'),
            message: _(
                'El cultivo entra en su etapa más sensible para el rendimiento final. Fecha estimada de floración: {{date}}.',
                { date: floweringDate }
            ),
            actions: [],
        };
    }

    // 2. In progress — until 5 days before the period ends.
    if (now.isBefore(nearEnd, 'day')) {
        return {
            title: _('Período crítico en curso'),
            message: _(
                'El cultivo está en su etapa más sensible para el rendimiento final. Fecha estimada de floración: {{date}}.',
                { date: floweringDate }
            ),
            actions: [],
        };
    }

    // 3. Ending — final 5 days. Prompt a visit to estimate yield; name the maturity date.
    const maturity = corn?.expectedMaturityDate ? dayjs(corn.expectedMaturityDate) : null;
    const maturityDate = maturity?.isValid() ? maturity.format('DD/MM/YYYY') : null;
    return {
        title: _('El período crítico está por finalizar'),
        message: maturityDate
            ? _(
                  'Coordiná una recorrida con el productor para monitorear y estimar el rinde. Fecha estimada de madurez fisiológica: {{date}}.',
                  { date: maturityDate }
              )
            : _('Coordiná una recorrida con el productor para monitorear y estimar el rinde.'),
        actions: [
            { label: _('Crear informe'), variant: 'contained', action: 'create-visit-report' },
            { label: _('Agendar visita'), variant: 'outlined', action: 'schedule-visit' },
        ],
    };
};
